import { create } from 'zustand';
import { regionsAPI } from '@/api/regions';
import { servicesAPI } from '@/api/services';
import type {
  Region,
  Service,
  ServiceWithPrice,
  RegionFilters,
  ServiceFilters,
} from '@/types';

interface RegionStore {
  // State
  regions: Region[];
  services: Service[];
  selectedRegion: Region | null;
  selectedService: Service | null;
  regionServices: ServiceWithPrice[];
  loading: boolean;
  error: string | null;
  
  // Pagination state for region services
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;

  // Region actions
  fetchRegions: (filters?: RegionFilters) => Promise<void>;
  fetchRegionById: (id: number) => Promise<void>;
  fetchRegionServices: (regionId: number, page?: number) => Promise<void>;
  setSelectedRegion: (region: Region | null) => void;
  setPage: (page: number) => void;
  clearRegions: () => void;

  // Service actions
  fetchServices: (filters?: ServiceFilters) => Promise<void>;
  fetchServiceById: (id: number) => Promise<void>;
  setSelectedService: (service: Service | null) => void;
  clearServices: () => void;

  // Utility actions
  clearError: () => void;
  clearAll: () => void;
}

export const useRegionStore = create<RegionStore>((set, get) => ({
  // Initial state
  regions: [],
  services: [],
  selectedRegion: null,
  selectedService: null,
  regionServices: [],
  loading: false,
  error: null,
  
  // Pagination state
  currentPage: 1,
  totalPages: 1,
  itemsPerPage: 20,
  totalItems: 0,

  // Region actions
  // Region actions
  fetchRegions: async (filters) => {
    set({ loading: true, error: null });
    try {
      // Fetch all regions by making multiple paginated requests
      let allRegions: Region[] = [];
      let page = 1;
      const limit = 100; // Max allowed by backend
      let hasMore = true;

      while (hasMore) {
        const response = await regionsAPI.getAll({
          ...filters,
          page,
          limit
        });
        
        const { regions, pagination } = response.data.data;
        allRegions = [...allRegions, ...regions];
        
        // Check if there are more pages
        hasMore = Boolean(pagination && page < pagination.pages);
        page++;
      }

      set({
        regions: allRegions,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.error?.message || 'فشل تحميل المناطق',
        loading: false,
      });
    }
  },
  fetchRegionById: async (id) => {
    set({ loading: true, error: null });
    try {
      const response = await regionsAPI.getById(id);
      set({
        selectedRegion: response.data.data.region,
        regionServices: response.data.data.services || [],
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.error?.message || 'فشل تحميل المنطقة',
        loading: false,
      });
    }
  },

  fetchRegionServices: async (regionId, page = 1) => {
    set({ loading: true, error: null });
    try {
      const { itemsPerPage } = get();
      const response = await regionsAPI.getServices(regionId, {
        page,
        limit: itemsPerPage
      });
      const { services, pagination } = response.data.data;
      set({
        regionServices: services,
        currentPage: pagination.currentPage,
        totalPages: pagination.totalPages,
        itemsPerPage: pagination.itemsPerPage,
        totalItems: pagination.totalItems,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.error?.message || 'فشل تحميل الخدمات',
        loading: false,
      });
    }
  },

  setSelectedRegion: (region) => {
    set({
      selectedRegion: region,
      currentPage: 1,
      regionServices: [],
    });
    if (region) {
      get().fetchRegionServices(region.id, 1);
    }
  },

  setPage: (page) => {
    const { selectedRegion } = get();
    if (selectedRegion) {
      set({ currentPage: page });
      get().fetchRegionServices(selectedRegion.id, page);
    }
  },

  clearRegions: () => {
    set({
      regions: [],
      selectedRegion: null,
      regionServices: [],
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
    });
  },

  // Service actions
  fetchServices: async (filters) => {
    set({ loading: true, error: null });
    try {
      const response = await servicesAPI.getAll(filters);
      set({
        services: response.data.data.services,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.error?.message || 'فشل تحميل الخدمات',
        loading: false,
      });
    }
  },

  fetchServiceById: async (id) => {
    set({ loading: true, error: null });
    try {
      const response = await servicesAPI.getById(id);
      set({
        selectedService: response.data.data.service,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.error?.message || 'فشل تحميل الخدمة',
        loading: false,
      });
    }
  },

  setSelectedService: (service) => {
    set({ selectedService: service });
  },

  clearServices: () => {
    set({
      services: [],
      selectedService: null,
    });
  },

  // Utility actions
  clearError: () => {
    set({ error: null });
  },

  clearAll: () => {
    set({
      regions: [],
      services: [],
      selectedRegion: null,
      selectedService: null,
      regionServices: [],
      loading: false,
      error: null,
    });
  },
}));
